import store from '../store';
import { setApiLanguageIdResolver } from '../utils/apiLanguageId.js';
import { startAttributionCaptureLifecycle, runAttributionCapture } from '../attribution/capture.js';
import { scheduleIdleWork } from '../utils/scheduleIdleWork.js';
import { mountRegisteredIslands } from './islandRegistry';

function watchLanguageChanges() {
    store.watch(
        () => store.state.lang?.id,
        (newId, oldId) => {
            if (oldId == null || newId == null || newId === oldId) return;
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('langId', String(newId));
            }
            store.dispatch('form/reloadCountries').catch(() => {});
        }
    );
}

function scheduleMapPopup() {
    const mapPopupEl = document.getElementById('map-popup-app');
    if (!mapPopupEl) return;

    const mount = () => {
        import(/* webpackChunkName: 'map-popup' */ '../components/MapPopup.vue').then(({ default: MapPopup }) => {
            if (!mapPopupEl.isConnected || mapPopupEl.__vue_app__) return;
            import('vue').then(({ createApp }) => {
                const app = createApp(MapPopup, {
                    imageSrc: mapPopupEl.getAttribute('data-image-src') || '',
                    caption: mapPopupEl.getAttribute('data-caption') || '',
                });
                app.use(store);
                import(/* webpackChunkName: 'svg-icon' */ '../components/SvgIcon.vue').then(({ default: SvgIcon }) => {
                    app.component('SvgIcon', SvgIcon);
                    app.component('svg-icon', SvgIcon);
                    app.mount(mapPopupEl);
                });
            });
        }).catch(() => {});
    };

    if (typeof IntersectionObserver === 'undefined') {
        scheduleIdleWork(mount);
        return;
    }
    const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        mount();
    }, { rootMargin: '240px' });
    observer.observe(mapPopupEl);
}

function scheduleHowitworksForm() {
    const el = document.getElementById('howitworks-validity-form-app');
    if (!el) return;

    const mount = () => {
        const parseJsonAttr = (raw) => {
            if (raw == null) return null;
            const trimmed = String(raw).trim();
            if (!trimmed || trimmed === 'null') return null;
            try { return JSON.parse(trimmed); } catch (_) { return null; }
        };
        const formProps = {
            validityCheckUrl: el.dataset.validityCheckUrl || '',
            defaultCountry: el.dataset.defaultCountry || '',
            countryLabel: el.dataset.countryLabel || 'Country of Registration',
            plateLabel: el.dataset.plateLabel || 'License Plate Number',
            buttonLabel: el.dataset.buttonLabel || 'Check Vignette',
            platePlaceholder: el.dataset.platePlaceholder || 'e.g. 1AB 1234',
            angleDownSolid: parseJsonAttr(el.dataset.angleDownSolid),
            euStarsImage: parseJsonAttr(el.dataset.euStars),
        };
        Promise.all([
            import('vue'),
            import(/* webpackChunkName: 'howitworks-validity-form' */ '../components/HowitworksValidityForm.vue'),
            import(/* webpackChunkName: 'svg-icon' */ '../components/SvgIcon.vue'),
        ]).then(([{ createApp }, { default: HowitworksValidityForm }, { default: SvgIcon }]) => {
            if (!el.isConnected || el.__vue_app__) return;
            const app = createApp(HowitworksValidityForm, formProps);
            app.use(store);
            app.component('SvgIcon', SvgIcon);
            app.component('svg-icon', SvgIcon);
            if (!store.state?.form?.countries?.length) {
                store.dispatch('form/loadInitialData').catch(() => {});
            }
            app.mount(el);
        }).catch(() => {});
    };

    if (typeof IntersectionObserver === 'undefined') {
        scheduleIdleWork(mount);
        return;
    }
    const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        mount();
    }, { rootMargin: '240px' });
    observer.observe(el);
}

/**
 * Boots the store and only the islands present in this document.
 * #app stays server-rendered HTML.
 */
export function runApplication() {
    if (typeof window !== 'undefined' && window.__VS_APP_BOOTED__) return;
    if (typeof window !== 'undefined') window.__VS_APP_BOOTED__ = true;

    const bootstrapAppData = typeof window !== 'undefined' ? window.__APP_DATA__ : null;
    store.dispatch('bootstrapFromWindow');

    if (bootstrapAppData) {
        const initialCurrencyCode = store.getters['currency/currentCurrency']?.code
            || bootstrapAppData.currency?.code;
        if (initialCurrencyCode && typeof window.switchCurrency === 'function') {
            window.switchCurrency(initialCurrencyCode);
        }
    }

    setApiLanguageIdResolver(() => {
        const id = store.state.lang?.id;
        if (id !== undefined && id !== null && id !== '') return id;
        if (typeof localStorage !== 'undefined') return localStorage.getItem('langId');
        return null;
    });

    const heroPricing = document.querySelector('#pricing-app[data-variant="hero"]');
    if (heroPricing) {
        store.dispatch('form/loadProductCatalog').catch(() => {});
    }

    watchLanguageChanges();
    startAttributionCaptureLifecycle();

    if (document.querySelector('buy-vignette, payment-processing')) {
        import(/* webpackChunkName: 'router' */ '../router.js').then(({ default: router }) => {
            router.afterEach(() => runAttributionCapture());
        }).catch(() => {});
    }

    mountRegisteredIslands();
    scheduleMapPopup();
    scheduleHowitworksForm();
}
